import { CurrencyPipe, JsonPipe, NgFor, NgIf, PercentPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import {
  EligibilityAccumulatorDto,
  EligibilityLookupResponse,
  EligibilityService,
  HdhpAssessmentDto,
  ServiceBenefitDesignDto,
} from '../../../services/eligibility.service';

type LookupMode = 'memberId' | 'demographics';

interface ServiceDesignGroup {
  category: string;
  label: string;
  designs: ServiceBenefitDesignDto[];
}

@Component({
  selector: 'app-eligibility-lookup',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    NgFor,
    CurrencyPipe,
    PercentPipe,
    JsonPipe,
    ButtonModule,
    CalendarModule,
    DropdownModule,
    InputTextModule,
    MessageModule,
    ProgressSpinnerModule,
    SelectButtonModule,
    TableModule,
    TagModule,
  ],
  templateUrl: './eligibility-lookup.component.html',
  styleUrl: './eligibility-lookup.component.scss',
})
export class EligibilityLookupComponent implements OnInit {
  sources: string[] = [];
  selectedSource?: string;

  lookupModes: { label: string; value: LookupMode }[] = [
    { label: 'Member ID', value: 'memberId' },
    { label: 'Name + DOB', value: 'demographics' },
  ];
  lookupMode: LookupMode = 'memberId';

  memberId = '';
  firstName = '';
  lastName = '';
  dateOfBirth?: Date;

  loading = false;
  errorMessage?: string;
  response?: EligibilityLookupResponse;
  accumulators: EligibilityAccumulatorDto[] = [];
  serviceDesignGroups: ServiceDesignGroup[] = [];
  subscriberLabel?: string;
  planStatus?: string;
  planName?: string;
  hdhpAssessment?: HdhpAssessmentDto;

  private readonly route = inject(ActivatedRoute, { optional: true });
  private readonly eligibilityService = inject(EligibilityService);

  private readonly categoryLabels: Record<string, string> = {
    AdvancedImaging: 'Advanced imaging (MRI / CT / PET)',
    BasicImaging: 'Basic imaging (X-ray / ultrasound)',
    Lab: 'Labwork',
    Mammogram: 'Mammograms',
    GiEndoscopy: 'Colonoscopy / endoscopy (STC proxy)',
    Other: 'Other services',
  };

  ngOnInit(): void {
    const routeSourceId = this.route?.snapshot.paramMap.get('sourceId') ?? undefined;
    const routeMemberId = this.route?.snapshot.paramMap.get('memberId') ?? undefined;

    if (routeSourceId) {
      this.selectedSource = routeSourceId;
    }
    if (routeMemberId) {
      this.lookupMode = 'memberId';
      this.memberId = routeMemberId;
    }

    this.eligibilityService.getSources()
      .then(sources => {
        this.sources = sources;
        if (!this.selectedSource) {
          this.selectedSource = sources[0];
        } else if (!sources.includes(this.selectedSource)) {
          this.sources = [this.selectedSource, ...sources];
        }

        if (routeSourceId && routeMemberId) {
          void this.lookup();
        }
      })
      .catch(() => {
        this.errorMessage = 'Unable to load eligibility sources.';
        if (routeSourceId && routeMemberId) {
          void this.lookup();
        }
      });
  }

  get canLookup(): boolean {
    if (!this.selectedSource || this.loading) return false;
    if (this.lookupMode === 'memberId') {
      return !!this.memberId.trim();
    }
    return !!this.firstName.trim() && !!this.lastName.trim() && !!this.dateOfBirth;
  }

  async lookup(): Promise<void> {
    if (!this.canLookup || !this.selectedSource) return;

    this.loading = true;
    this.errorMessage = undefined;
    this.response = undefined;
    this.accumulators = [];
    this.serviceDesignGroups = [];
    this.subscriberLabel = undefined;
    this.planStatus = undefined;
    this.planName = undefined;
    this.hdhpAssessment = undefined;

    try {
      const dob = this.dateOfBirth
        ? this.toDateOnly(this.dateOfBirth)
        : undefined;

      const response = await this.eligibilityService.lookup({
        sourceId: this.selectedSource,
        ...(this.lookupMode === 'memberId'
          ? { memberId: this.memberId.trim() }
          : {
              firstName: this.firstName.trim(),
              lastName: this.lastName.trim(),
              dateOfBirth: dob,
            }),
        ...(this.lookupMode === 'memberId' && this.firstName.trim()
          ? { firstName: this.firstName.trim() }
          : {}),
        ...(this.lookupMode === 'memberId' && this.lastName.trim()
          ? { lastName: this.lastName.trim() }
          : {}),
        ...(this.lookupMode === 'memberId' && dob
          ? { dateOfBirth: dob }
          : {}),
      }, true);

      this.applyResponse(response);
    } catch (err: any) {
      if (typeof err === 'string') {
        this.errorMessage = err;
      } else if (err?.message) {
        this.errorMessage = err.message;
      } else {
        this.errorMessage = 'Eligibility lookup failed.';
      }
    } finally {
      this.loading = false;
    }
  }

  categoryNote(category: string): string | undefined {
    if (category === 'GiEndoscopy') {
      return 'No dedicated colonoscopy/endoscopy STC — these rows are coarse surgical/outpatient proxies.';
    }
    if (category === 'AdvancedImaging') {
      return 'STC 62 typically covers MRI and CT together; PET may appear here or under basic imaging depending on the payer.';
    }
    return undefined;
  }

  coinsuranceDisplay(percent?: number | null): number | null {
    if (percent == null) return null;
    return percent <= 1 ? percent : percent / 100;
  }

  get showHdhpTag(): boolean {
    const status = this.hdhpAssessment?.status;
    return status === 'Named' || status === 'LikelyQualified';
  }

  private applyResponse(response: EligibilityLookupResponse): void {
    this.response = response;
    this.accumulators = response.accumulators ?? [];
    this.planStatus = response.planStatus ?? undefined;
    this.planName = response.planName ?? undefined;
    this.hdhpAssessment = response.hdhpAssessment ?? undefined;
    this.subscriberLabel = [response.subscriberName, response.memberId]
      .filter(Boolean)
      .join(' · ') || undefined;

    const designs = response.serviceDesigns ?? [];
    const byCategory = new Map<string, ServiceBenefitDesignDto[]>();
    for (const design of designs) {
      const key = design.category || 'Other';
      const list = byCategory.get(key) ?? [];
      list.push(design);
      byCategory.set(key, list);
    }

    const order = ['AdvancedImaging', 'BasicImaging', 'Lab', 'Mammogram', 'GiEndoscopy', 'Other'];
    this.serviceDesignGroups = order
      .filter(key => byCategory.has(key))
      .map(key => ({
        category: key,
        label: this.categoryLabels[key] ?? key,
        designs: byCategory.get(key)!,
      }));
  }

  private toDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
